import React, { useContext, useState } from 'react';
import { Drawer, DrawerHeaderLeft } from './Appbar';
import { Avatar, CircularProgress, Divider, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';

import { useTheme } from '@mui/material/styles';
import { ReactComponent as BrandLogo } from 'assets/imagotipo.svg';
import { GlobalContext } from 'GlobalContext';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import ArticleIcon from '@mui/icons-material/Article';
import DifferenceIcon from '@mui/icons-material/Difference';
import LaunchIcon from '@mui/icons-material/Launch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logout } from 'utils/Login';
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
    views: string[];
}

interface TkConfigItem {
    key: string;
    value: string | null;
}

const Left: React.FC = () => {
    const { openLeft, setAuthenticated } = useContext(GlobalContext);

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const [loadingLogout, setLoadingLogout] = useState(false);

    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const logoStyles = {
        maxWidth: '100%',
        maxHeight: '50px',
        width: 'auto',
        height: 'auto',
        '--letters-fill': isDarkMode ? '#ffffff' : '#121212',
        '--background-fill': theme.palette.primary.main,
    };

    const tkConfigSystem: TkConfigItem[] = (() => {
        const rawConfig = localStorage.getItem('tk_config_system');
        if (!rawConfig) {
            return [];
        }

        try {
            const parsed = JSON.parse(rawConfig);
            return Array.isArray(parsed) ? (parsed as TkConfigItem[]) : [];
        } catch {
            return [];
        }
    })();

    const tatDashboardBaseUrl = tkConfigSystem.find((c) => c.key === 'base_url_tat_dashboard')?.value ?? null;
    const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'es';
    const loggedUsername = (localStorage.getItem('username') || '').trim();
    const tatSsoUrl = (() => {
        if (!tatDashboardBaseUrl) {
            return '';
        }

        try {
            const targetUrl = new URL(tatDashboardBaseUrl);
            targetUrl.pathname = `/${locale}/sso/start/tk-dev`;

            if (loggedUsername) {
                targetUrl.searchParams.set('authuser', loggedUsername);
            }

            return targetUrl.toString();
        } catch (error) {
            console.warn('No se pudo construir la URL de MyPalTime con REACT_APP_TAT_DASHBOARD_BASE_URL', error);
            return '';
        }
    })();

    const rebrandingBaseUrl = tkConfigSystem.find((c) => c.key === 'base_url_rebranding_dashboard')?.value?.replace(/\/+$/, '') ?? null;
    const accountUrl = process.env.REACT_APP_TK_GW_DOMAIN ? `${process.env.REACT_APP_TK_GW_DOMAIN}/v1/auth/v1/auth/account` : '';

    const handleOpenTkRebranding = () => {
        if (!rebrandingBaseUrl) return;
        let targetUrl = `${rebrandingBaseUrl}/oauth`;
        if (loggedUsername) {
            const separator = targetUrl.includes('?') ? '&' : '?';
            targetUrl = `${targetUrl}${separator}authuser=${encodeURIComponent(loggedUsername)}`;
        }

        const popup = window.open(targetUrl, '_blank', 'noopener,noreferrer');
        if (!popup) {
            console.warn('El navegador bloqueó la apertura de la pestaña de TK Rebranding.');
        }
    };

    const handleOpenAccountNewTab = () => {
        if (!accountUrl) return;
        window.open(accountUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <>
            <Drawer variant="permanent" open={openLeft}>
                <DrawerHeaderLeft>
                    {openLeft ? (
                        <>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    padding: '10px 10px 10px 10px',
                                }}
                            >
                                <BrandLogo aria-label="logo-app" style={logoStyles} />
                            </div>
                        </>
                    ) : null}
                </DrawerHeaderLeft>

                <Divider />

                <List>
                    <ListItemButton sx={{ pl: 2.5 }} selected={location.pathname === '/'} onClick={() => navigate('/')}>
                        <ListItemIcon sx={{ minWidth: '35px' }}>
                            <HomeIcon />
                        </ListItemIcon>

                        <ListItemText primary={t('home.title')} />
                    </ListItemButton>

                    {localStorage.getItem('access_token') && jwtDecode<DecodedToken>(localStorage.getItem('access_token')!)?.views.some((view) => /\/settings/.test(view)) && (
                        <ListItemButton sx={{ pl: 2.5 }} selected={location.pathname.startsWith('/settings')} onClick={() => navigate('/settings/resume')}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                                <SettingsIcon />
                            </ListItemIcon>

                            <ListItemText primary={t('settings.title')} />
                        </ListItemButton>
                    )}

                    {localStorage.getItem('access_token') && jwtDecode<DecodedToken>(localStorage.getItem('access_token')!)?.views.some((view) => /\/timekeeper/.test(view)) && (
                        <ListItemButton sx={{ pl: 2.5 }} selected={location.pathname.startsWith('/timekeeper')} onClick={() => navigate('/timekeeper/resume')}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                                <AutoGraphIcon />
                            </ListItemIcon>

                            <ListItemText primary={t('timekeeper.title')} />
                        </ListItemButton>
                    )}

                    {localStorage.getItem('access_token') && jwtDecode<DecodedToken>(localStorage.getItem('access_token')!)?.views.some((view) => /\/user_management/.test(view)) && (
                        <ListItemButton sx={{ pl: 2.5 }} selected={location.pathname.startsWith('/user_management')} onClick={() => navigate('/user_management/resume')}>
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                                <SwitchAccountIcon />
                            </ListItemIcon>

                            <ListItemText primary={t('user_management.title')} />
                        </ListItemButton>
                    )}
                    {localStorage.getItem('access_token') && jwtDecode<DecodedToken>(localStorage.getItem('access_token')!)?.views.some((view) => /\/custom_reports/.test(view)) && (
                        <ListItemButton
                            sx={{
                                pl: 2.5,
                                whiteSpace: 'normal',
                                '& .MuiListItemText-primary': {
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                    lineHeight: 1.2,
                                },
                            }}
                            selected={location.pathname.startsWith('/custom_reports')}
                            onClick={() => navigate('/custom_reports/resume')}
                        >
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                                <ArticleIcon />
                            </ListItemIcon>

                            <ListItemText primary={t('custom_reports.title')} />
                        </ListItemButton>
                    )}
                    {
                        //localStorage.getItem('access_token') && jwtDecode<DecodedToken>(localStorage.getItem('access_token')!)?.views.some(view => /\/accruals/.test(view)) &&
                        <ListItemButton
                            sx={{
                                pl: 2.5,
                                whiteSpace: 'normal',
                                '& .MuiListItemText-primary': {
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                    lineHeight: 1.2,
                                },
                            }}
                            selected={location.pathname.startsWith('/accruals')}
                            onClick={() => navigate('/accruals/accrual_codes')}
                        >
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                                <DifferenceIcon />
                            </ListItemIcon>

                            <ListItemText primary={t('accruals.title')} />
                        </ListItemButton>
                    }
                    {
                        //localStorage.getItem('access_token') && jwtDecode<DecodedToken>(localStorage.getItem('access_token')!)?.views.some(view => /\/accruals/.test(view)) &&
                        <ListItemButton
                            sx={{
                                pl: 2.5,
                                whiteSpace: 'normal',
                                '& .MuiListItemText-primary': {
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                    lineHeight: 1.2,
                                },
                            }}
                            selected={location.pathname.startsWith('/schedule-rules')}
                            onClick={() => navigate('/schedule-rules/shift-profile')}
                        >
                            <ListItemIcon sx={{ minWidth: '35px' }}>
                                <ManageHistoryIcon />
                            </ListItemIcon>

                            <ListItemText primary={'Reglas de Horario'} />
                        </ListItemButton>
                    }

                    <ListItemButton sx={{ pl: 2.5 }} disabled={!tatSsoUrl} onClick={() => window.open(tatSsoUrl, '_blank', 'noopener,noreferrer')}>
                        <ListItemIcon sx={{ minWidth: '35px' }}>
                            <LaunchIcon />
                        </ListItemIcon>
                        <ListItemText primary={'MyPalTime'} />
                    </ListItemButton>

                    <ListItemButton
                        sx={{ pl: 2.5, my: 0.5, borderRadius: 1.5 }}
                        className="bg-linear-to-r from-yellow-200 via-amber-300 to-yellow-200 text-zinc-900 shadow-[0_0_18px_rgba(251,191,36,0.8)] animate-pulse"
                        disabled={!rebrandingBaseUrl}
                        onClick={handleOpenTkRebranding}
                    >
                        <ListItemIcon sx={{ minWidth: '35px', color: 'inherit' }}>
                            <AutoAwesomeIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary={'TK Rebranding'}
                            sx={{
                                '& .MuiListItemText-primary': {
                                    fontWeight: 800,
                                    letterSpacing: 0.3,
                                },
                            }}
                        />
                    </ListItemButton>
                </List>
                <List sx={{ position: 'absolute', bottom: 0, width: '100%' }}>
                    <Divider />

                    <ListItem
                        secondaryAction={
                            openLeft ? (
                                <IconButton disabled={loadingLogout} onClick={() => logout(setLoadingLogout, setAuthenticated)}>
                                    {loadingLogout ? <CircularProgress size={20} color="error" /> : <ExitToAppIcon color="error" />}
                                </IconButton>
                            ) : null
                        }
                    >
                        <ListItemIcon sx={{ ml: -0.5 }}>
                            <IconButton onClick={handleOpenAccountNewTab}>
                                <Avatar sx={{ width: 32, height: 32 }}>
                                    <PersonIcon />
                                </Avatar>
                            </IconButton>
                        </ListItemIcon>

                        <ListItemText primary={localStorage.getItem('username')} secondary={'profile'} />
                    </ListItem>
                </List>
            </Drawer>

        </>
    );
};

export { Left };
